import { RacemapAPIClient } from '@racemap/utilities/api-client';
import type { Images } from '@racemap/utilities/types/events';
import { useState } from 'react';
import FileDialog from '../FileDialog';

interface Options {
  allowedItemTypes?: Array<string>;
  imageClass?: 'logo' | 'banner' | 'poiIcon' | 'sponsorLogo';
}

interface State {
  status: 'idle' | 'loading' | 'success' | 'error';
  error: Error | null;
}

interface Return {
  uris: Images | null;
  error: Error | null;
}

const apiClient = RacemapAPIClient.fromWindowLocation();

export const useImageUpload = ({
  allowedItemTypes = ['image/png', 'image/jpeg', 'image/gif'],
  imageClass = 'logo',
}: Options = {}): {
  uploadImage: () => Promise<Return>;
  state: { status: 'idle' | 'loading' | 'success' | 'error'; error: Error | null };
} => {
  const [state, setState] = useState<State>({
    status: 'idle',
    error: null,
  });

  const uploadImage = async (): Promise<Return> => {
    try {
      setState({
        status: 'loading',
        error: null,
      });

      const files = await FileDialog.show({ accept: allowedItemTypes?.join(','), multiple: false });
      const image = files[0];

      const uris = await apiClient.uploadEventImage({ [imageClass]: image } as Record<
        'logo' | 'banner' | 'poiIcon' | 'sponsorLogo',
        File
      >);

      setState({
        status: 'success',
        error: null,
      });

      return { uris, error: null };
    } catch (error) {
      if (error instanceof Error) {
        setState({
          status: 'error',
          error,
        });

        return { uris: null, error };
      }

      throw error;
    }
  };

  return {
    uploadImage,
    state,
  };
};
